import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Users,
  MessageCircle,
  Shield,
  MoreVertical,
  Settings,
  Upload,
  SquarePen,
  Globe,
  Lock,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import useModal from '../hooks/useModal';
import useConfirmDialog from '../hooks/useConfirmDialog';
import {
  useConvertRoomVisibility,
  useUpdateRoom,
  useAssignRoomAdmin,
} from '../hooks/queries/useSpaceQueries';
import { getImageUrl } from '../utils/imageUrl';
import { IRoomDetail } from '../types';
import { formatDate } from '../utils/formatDate';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import EditRoomDialog from './EditRoomDialog';
import ImagePreviewOverlay from './ImagePreviewOverlay';

interface IChatroomDetailProps {
  roomId: string;
  onBack: (spaceId?: string) => void;
  onViewMembers: (roomId: string) => void;
  onViewMessages: (roomId: string) => void;
}

const ChatroomDetail = ({
  roomId,
  onBack,
  onViewMembers,
  onViewMessages,
}: IChatroomDetailProps) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<IRoomDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const editDialog = useModal();
  const confirmDialog = useConfirmDialog();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const convertVisibility = useConvertRoomVisibility();
  const updateRoom = useUpdateRoom();
  const updateRoomAdmins = useAssignRoomAdmin();

  useEffect(() => {
    loadDetail();
  }, [roomId]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const data = await api.get<IRoomDetail>(
        `/backoffice/rooms/${roomId}/detail`,
      );
      setDetail(data);
      setIsPublic(!data.isPrivate);
      setIsAnnouncement(data.isAnnouncement || false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAnnouncement = () => {
    if (!detail) return;

    const newValue = !isAnnouncement;
    const confirmMsg = newValue
      ? t('chatroom.confirm_set_announcement')
      : t('chatroom.confirm_unset_announcement');

    confirmDialog.show(confirmMsg, async () => {
      setIsAnnouncement(newValue);
      updateRoom.mutate(
        { roomId, data: { isAnnouncement: newValue } },
        {
          onSuccess: () => {
            loadDetail();
          },
        },
      );
    });
  };

  const handleConvertVisibility = async () => {
    if (!detail) return;

    const toPrivate = isPublic; // currently public → convert to private
    const confirmMsg = toPrivate
      ? t('chatroom.confirm_convert_private')
      : t('chatroom.confirm_convert_public');

    confirmDialog.show(confirmMsg, async () => {
      convertVisibility.mutate(
        { roomId, isPrivate: toPrivate },
        {
          onSuccess: () => {
            loadDetail();
          },
        },
      );
    });
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        {t('common.no_data')}
      </div>
    );
  }

  const stats = [
    {
      label: t('chatroom.total_members'),
      value: detail.memberCount.toLocaleString(),
      icon: <Users className="w-8 h-8 text-green-600" />,
    },
    {
      label: t('chatroom.total_messages'),
      value: detail.messageCount.toLocaleString(),
      icon: <MessageCircle className="w-8 h-8 text-purple-600" />,
    },
    {
      label: t('chatroom.admin_count'),
      value: detail.adminMembers.length.toString(),
      icon: <Shield className="w-8 h-8 text-blue-600" />,
    },
  ];

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => {
          onBack(detail.spaceId || undefined);
        }}
        className="flex items-center gap-1 text-sm text-[#0A0A0A] hover:text-foreground w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> {t('chatroom.back_to_space')}
      </Button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`w-16 h-16 rounded-lg bg-purple-500 flex items-center justify-center overflow-hidden${detail.avatar ? ' cursor-pointer' : ''}`}
            onClick={() => {
              if (detail.avatar) setPreviewImage(getImageUrl(detail.avatar)!);
            }}
          >
            {detail.avatar ? (
              <img
                src={getImageUrl(detail.avatar)!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <MessageCircle className="w-10 h-10 text-white" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {detail.name}
              </h1>
              <Badge
                variant={detail.isPrivate ? 'muted' : 'success'}
                className="rounded-full"
              >
                {detail.isPrivate ? t('common.private') : t('common.public')}
              </Badge>
              {detail.isDirectMessage && (
                <Badge variant="outline" className="rounded-full">
                  {detail.spaceId
                    ? t('chatroom.owner_dm')
                    : t('chatroom.friend_dm')}
                </Badge>
              )}
              {detail.isLocked && (
                <Badge variant="destructive" className="rounded-full">
                  {t('chatroom.locked')}
                </Badge>
              )}
            </div>
            <p className="text-gray-600 mt-2">{detail.space?.name || '—'}</p>
            <div className="flex items-center gap-6 mt-3 text-sm text-gray-600">
              <span>
                {t('chatroom.created_on', {
                  date: formatDate(detail.createdAt),
                })}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => editDialog.open()}>
            <SquarePen className="w-4 h-4" />
            {t('chatroom.edit_room')}
          </Button>
          <Button variant="outline" onClick={() => onViewMessages(roomId)}>
            <MessageCircle className="w-4 h-4" />
            {t('chatroom.view_messages')}
          </Button>
          <Button variant="outline" onClick={() => onViewMembers(roomId)}>
            <Users className="w-4 h-4" />
            {t('chatroom.view_all_members')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map(({ label, value, icon }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {value}
                  </p>
                </div>
                {icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Settings Section */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-700" />{' '}
            {t('chatroom.settings')}
          </h2>

          {/* Avatar */}
          <div className="flex items-start gap-4 mb-6 pb-6 border-b border-border">
            <div
              className={`w-20 h-20 rounded-lg bg-linear-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold overflow-hidden shrink-0${detail.avatar ? ' cursor-pointer' : ''}`}
              onClick={() => {
                if (detail.avatar) setPreviewImage(getImageUrl(detail.avatar)!);
              }}
            >
              {detail.avatar ? (
                <img
                  src={getImageUrl(detail.avatar)!}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                detail.name.charAt(0)
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                {t('chatroom.room_avatar')}
              </div>
              <div className="text-xs text-gray-500 mt-1 mb-3">
                {t('space_settings.avatar_hint')}
              </div>
              <label className="inline-flex">
                <Button variant="outline" size="sm" className="gap-2" asChild>
                  <span>
                    <Upload className="w-4 h-4" />
                    {t('space_settings.upload_avatar')}
                  </span>
                </Button>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const result = await api.upload<{ imageUrl: string }>(
                        '/backoffice/upload-image',
                        file,
                      );
                      updateRoom.mutate(
                        { roomId, data: { avatar: result.imageUrl } },
                        {
                          onSuccess: () => {
                            loadDetail();
                          },
                        },
                      );
                    } catch (err: any) {
                      toast.error(
                        err.message || t('chatroom.avatar_upload_failed'),
                      );
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>

          {/* Description */}
          <div className="mb-6 pb-6 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {t('chatroom.description')}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              {detail.description || t('chatroom.no_description')}
            </p>
          </div>

          {/* Room Type */}
          <div className="mb-6 pb-6 border-b border-border">
            <div className="text-sm font-medium text-gray-700 mb-3">
              {t('chatroom.room_type')}
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {isPublic ? (
                  <Globe className="w-5 h-5 text-blue-600" />
                ) : (
                  <Lock className="w-5 h-5 text-purple-600" />
                )}
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {isPublic
                      ? t('chatroom.public_room')
                      : t('chatroom.private_room')}
                  </div>
                  <div className="text-xs text-gray-500">
                    {isPublic
                      ? t('chatroom.public_room_desc')
                      : t('chatroom.private_room_desc')}
                  </div>
                </div>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={() => handleConvertVisibility()}
                disabled={convertVisibility.isPending || !detail.spaceId}
              />
            </div>
            {convertVisibility.isPending && (
              <div className="mt-2 text-xs text-blue-500">
                {t('chatroom.converting_visibility')}
              </div>
            )}
          </div>

          {/* Announcement Settings */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-3">
              {t('chatroom.announcement_settings')}
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {isAnnouncement ? (
                  <VolumeX className="w-5 h-5 text-red-600" />
                ) : (
                  <Volume2 className="w-5 h-5 text-green-600" />
                )}
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {isAnnouncement
                      ? t('chatroom.announcement_enabled')
                      : t('chatroom.set_as_announcement')}
                  </div>
                  <div className="text-xs text-gray-500">
                    {isAnnouncement
                      ? t('chatroom.only_admins_can_send')
                      : t('chatroom.all_members_can_send')}
                  </div>
                </div>
              </div>
              <Switch
                checked={isAnnouncement}
                onCheckedChange={() => handleToggleAnnouncement()}
                disabled={updateRoom.isPending}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admins Section */}
      <Card className="gap-0 overflow-hidden">
        <CardContent className="p-6 pb-0">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" /> {t('chatroom.room_admins')}
          </h2>
        </CardContent>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6">
                  {t('chatroom.admin_name')}
                </TableHead>
                <TableHead className="px-6">{t('chatroom.role')}</TableHead>
                <TableHead className="px-6">
                  {t('chatroom.assigned_date')}
                </TableHead>
                <TableHead className="px-6 text-right">
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.adminMembers.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell className="px-6">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-medium shrink-0 overflow-hidden"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        {admin.user?.avatar ? (
                          <img
                            src={getImageUrl(admin.user?.avatar)!}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          (admin.user?.displayName || '?').charAt(0)
                        )}
                      </div>
                      <div>
                        <button
                          onClick={() => {
                            window.location.href = `/users/${admin.userId}`;
                          }}
                          className="text-sm font-medium text-foreground hover:text-blue-600 hover:underline"
                        >
                          {admin.user?.displayName || t('rooms.unknown_user')}
                        </button>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {admin.user?.username || '—'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-6">
                    <Badge
                      variant={
                        admin.role === 'owner'
                          ? 'info'
                          : admin.role === 'space_admin'
                            ? 'purple'
                            : 'purple'
                      }
                      className="rounded-full"
                    >
                      {admin.role === 'owner'
                        ? t('chatroom.role_owner')
                        : admin.role === 'space_admin'
                          ? t('chatroom.role_space_admin')
                          : t('chatroom.role_admin')}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 text-sm text-muted-foreground">
                    {formatDate(admin.joinedAt)}
                  </TableCell>
                  <TableCell className="px-6 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-35">
                        <DropdownMenuItem
                          onClick={() => {
                            window.location.href = `/users/${admin.userId}`;
                          }}
                        >
                          {t('chatroom.view_profile')}
                        </DropdownMenuItem>
                        {admin.role === 'admin' && (
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => {
                              const name = admin.user?.displayName || '';
                              confirmDialog.show(
                                t('chatroom.confirm_remove_admin', { name }),
                                async () => {
                                  try {
                                    const allMembers = await api.get<
                                      { userId: string; role: string }[]
                                    >(`/backoffice/rooms/${roomId}/members`);
                                    const newAdminIds = allMembers
                                      .filter(
                                        (m) =>
                                          m.role === 'admin' &&
                                          m.userId !== admin.userId,
                                      )
                                      .map((m) => m.userId);
                                    updateRoomAdmins.mutate(
                                      { roomId, userIds: newAdminIds },
                                      { onSuccess: () => loadDetail() },
                                    );
                                  } catch (e: any) {
                                    toast.error(
                                      e.message || t('common.operation_failed'),
                                    );
                                  }
                                },
                              );
                            }}
                          >
                            {t('chatroom.remove_admin')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {detail.adminMembers.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="px-6 py-8 text-center text-sm text-muted-foreground"
                  >
                    {t('rooms.not_assigned')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Members Preview Section */}
      <Card className="gap-0 overflow-hidden">
        <CardContent className="p-6 pb-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5" /> {t('chatroom.room_members')}
            </h2>
            <Button
              variant="outline"
              onClick={() => onViewMembers(roomId)}
              className="text-sm"
            >
              {t('chatroom.view_all')}
            </Button>
          </div>
        </CardContent>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6">
                  {t('chatroom.member_name')}
                </TableHead>
                <TableHead className="px-6">{t('common.role')}</TableHead>
                <TableHead className="px-6">
                  {t('chatroom.join_date')}
                </TableHead>
                <TableHead className="px-6">
                  {t('chatroom.message_count')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.recentMembers
                .filter(
                  (member) =>
                    !detail.adminMembers.some(
                      (admin) => admin.userId === member.userId,
                    ),
                )
                .map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="px-6">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-medium shrink-0 overflow-hidden"
                          style={{ backgroundColor: '#3b82f6' }}
                        >
                          {member.user?.avatar ? (
                            <img
                              src={getImageUrl(member.user?.avatar)!}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            (member.user?.displayName || '?').charAt(0)
                          )}
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              window.location.href = `/users/${member.userId}`;
                            }}
                            className="text-sm font-medium text-foreground hover:text-blue-600 hover:underline"
                          >
                            {member.user?.displayName ||
                              t('rooms.unknown_user')}
                          </button>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {member.user?.username || '—'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-6">
                      <Badge
                        variant={
                          member.role === 'owner'
                            ? 'info'
                            : member.role === 'space_admin'
                              ? 'purple'
                              : member.role === 'admin'
                                ? 'purple'
                                : 'muted'
                        }
                        className="rounded-full"
                      >
                        {member.role === 'owner'
                          ? t('chatroom.role_owner')
                          : member.role === 'space_admin'
                            ? t('chatroom.role_space_admin')
                            : member.role === 'admin'
                              ? t('chatroom.role_admin')
                              : t('chatroom.role_member')}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-6 text-sm text-muted-foreground">
                      {formatDate(member.joinedAt)}
                    </TableCell>
                    <TableCell className="px-6 text-sm text-muted-foreground">
                      —
                    </TableCell>
                  </TableRow>
                ))}
              {detail.recentMembers.filter(
                (m) => !detail.adminMembers.some((a) => a.userId === m.userId),
              ).length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="px-6 py-8 text-center text-sm text-muted-foreground"
                  >
                    {t('rooms.no_members')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Room Dialog */}
      <EditRoomDialog
        isOpen={editDialog.isOpen}
        onClose={() => {
          editDialog.close();
          loadDetail();
        }}
        room={detail}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => !open && confirmDialog.cancel()}
      >
        <DialogContent className="sm:max-w-105">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={confirmDialog.cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDialog.confirm}
              disabled={confirmDialog.isLoading}
            >
              {confirmDialog.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Overlay */}
      {previewImage && (
        <ImagePreviewOverlay
          src={previewImage}
          alt={detail.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default ChatroomDetail;
